// GeoIP Service for Country Detection
export interface GeoIPResponse {
  country: string;
  countryCode: string;
  city?: string;
  region?: string;
  timezone?: string;
  ip?: string;
}

// Free GeoIP services (you can use any of these)
const GEOIP_SERVICES = [
  'https://ipapi.co/json/',
  'https://ip-api.com/json/',
  'https://api.country.is/',
  'https://ipinfo.io/json'
];

// Cache for GeoIP responses to avoid repeated requests
const geoIPCache = new Map<string, GeoIPResponse>();

export async function detectUserCountry(): Promise<string | null> {
  try {
    // Check if we're on the server side
    if (typeof window === 'undefined') {
      return null;
    }

    // Try to get from cache first
    const cacheKey = 'user-country';
    const cached = geoIPCache.get(cacheKey);
    if (cached) {
      return cached.countryCode;
    }

    // Try multiple services for reliability
    for (const service of GEOIP_SERVICES) {
      try {
        const response = await fetch(service, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) continue;

        const data = await response.json();
        let countryCode: string | null = null;

        // Parse response based on service
        if (service.includes('ipapi.co')) {
          countryCode = data.country_code;
        } else if (service.includes('ip-api.com')) {
          countryCode = data.countryCode;
        } else if (service.includes('country.is')) {
          countryCode = data.country;
        } else if (service.includes('ipinfo.io')) {
          countryCode = data.country;
        }

        if (countryCode) {
          const geoData: GeoIPResponse = {
            country: data.country_name || data.country || 'Unknown',
            countryCode: countryCode.toUpperCase(),
            city: data.city,
            region: data.region,
            timezone: data.timezone,
            ip: data.ip
          };

          // Cache the result
          geoIPCache.set(cacheKey, geoData);
          
          return countryCode.toUpperCase();
        }
      } catch (error) {
        console.warn(`GeoIP service ${service} failed:`, error);
        continue;
      }
    }

    return null;
  } catch (error) {
    console.error('Error detecting user country:', error);
    return null;
  }
}

// Alternative: Use browser's built-in locale detection
export function detectBrowserLocale(): string {
  if (typeof window === 'undefined') return 'en';
  
  const browserLocale = navigator.language || (navigator as any).userLanguage;
  const locale = browserLocale.split('-')[0].toLowerCase();
  
  // Map browser locales to our supported languages
  const localeMap: Record<string, string> = {
    'en': 'en',
    'fr': 'fr'
  };
  
  return localeMap[locale] || 'en';
}

// Get user's preferred language with fallback
export async function getUserPreferredLanguage(): Promise<string> {
  // First, check localStorage for user preference
  if (typeof window !== 'undefined') {
    const savedPreference = localStorage.getItem('user-language-preference');
    if (savedPreference && ['en', 'fr'].includes(savedPreference)) {
      return savedPreference;
    }
  }

  // Try to detect country and map to language
  const countryCode = await detectUserCountry();
  if (countryCode) {
    const { getLanguageFromCountry } = await import('./country-lang-mapping');
    return getLanguageFromCountry(countryCode);
  }

  // Fallback to browser locale
  return detectBrowserLocale();
}

// Save user language preference
export function saveUserLanguagePreference(language: string): void {
  if (typeof window !== 'undefined' && ['en', 'fr'].includes(language)) {
    localStorage.setItem('user-language-preference', language);
  }
}

// Clear user language preference (for testing)
export function clearUserLanguagePreference(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('user-language-preference');
  }
}
